import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { ObservableMedia } from '@angular/flex-layout';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatPaginator, MatSort } from '@angular/material';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { DataTableSource } from '../../../../classes/DataTableSource/datatablesource';
import { CollegeSchoolYearPipe } from '../../../../pipes/collegeschoolyear.pipe';
import { CollegeTermPipe } from '../../../../pipes/collegeterm.pipe';
import { CollegeInfo, CollegeTermInfo, ApplicationService, CollegeContactInfo, CollegeCourseInfo } from '../../../../services/application/application.service';
import { DialogService } from '../../../../services/dialogs/dialogs.service';
import { ErrorService } from '../../../../services/error/error.service';
import { SplashService } from '../../../../services/splash/splash.service';
import { UserInfo, UserService } from '../../../../services/user/user.service';


@Component({
    selector: 'app-college-contact',
    styleUrls: ['college-contact.component.scss'],
    templateUrl: 'college-contact.component.html',
})
export class CollegeContactComponent implements OnInit {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;
    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = ['CollegeName', 'Email', 'Actions'];
    CollegeContactData: DataTableSource<CollegeContactInfo>;
    CollegeContactForm: FormGroup;
    CollegeContacts: CollegeContactInfo[];
    Colleges: CollegeInfo[];
    IsEditContact: boolean;
    NoData: boolean;
    PartialViewReady: boolean;
    ShowingContactEntryPanel: boolean;
    SmallDisplay: boolean;
    User: UserInfo | null;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private collegeSchoolYearPipe: CollegeSchoolYearPipe,
        private collegeTermPipe: CollegeTermPipe,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService
    ) {}
    
    ngOnInit() {
        const self = this;
        
        self.NoData = false;
        self.PartialViewReady = false;
        self.ShowingContactEntryPanel = false;
        self.CollegeContactData = new DataTableSource<CollegeContactInfo>([], self.paginator, self.sorter);
        self.CollegeContacts = [];
        self.Colleges = [];
        self.IsEditContact = false;
        self.User = null;
        self.VisibleHeaders = [];
        self.VisibleColumns = [];
        
        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Setup the form group
        self.CollegeContactForm = self.fb.group({
            CollegeContactID: [null],
            CollegeID: [null, Validators.required],
            Email: ['', [Validators.required, Validators.pattern(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)]],
        });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const collegeObs = self.appService.GetAllColleges(self.User.AuthToken);
            const collegeContactsObs = self.appService.GetAllCollegeContacts(self.User.AuthToken);

            combineLatest(collegeObs, collegeContactsObs, function(
                collegeObs,
                collegeContactsObs
            ) {
                return {
                    Colleges: collegeObs,
                    CollegeContacts: collegeContactsObs
                };
            })
            .pipe(takeUntil(self._subrelease))
            .subscribe({
                next: (packet: any) => {
                    // self.Colleges = packet.Colleges;
                    self.CollegeContacts = packet.CollegeContacts;
                    self.Colleges = packet.Colleges;

                    if (!self.CollegeContacts || !self.CollegeContacts.length) {
                        // self.NoData = true;

                        self.CollegeContactData.Update([]);
                    } else {
                        // self.NoData = false;
                        self.CollegeContactData.Update(self.CollegeContacts);
                    }

                    self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                    self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                    // Ready for component use.
                    self.PartialViewReady = true;
                },
                error: (error: Error) => self.errorService.HandleServiceError(error)
            });
        });
    }

    // #region Add College Contact Method

    AddCollegeContact() {
        const self = this;

        const form = <CollegeContactForm>self.CollegeContactForm.getRawValue();
        if (!form) {
            return;
        }

        const collegeID = form.CollegeID;
        const email = form.Email;

        const college = self.Colleges.find(x => x.CollegeID == collegeID)!.CollegeName;

        if (!college) {
            return self.splashService.SnackbarError(`Could not find college with id ${collegeID}`);
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to add the following new college contact info to DELTA:<br /><br />
                        <strong>College: </strong>${college}<br />
                        <strong>Email Contact: </strong>${email}<br /><br />
                    Once added, this contact info will be added to the list of college contact emails that will receive advisor email notifications. 
                    <br /><br />
                    Are you sure you wish to continue?`,
                OKText: 'Yes - Add Contact',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the contact info to the system.
                    self.splashService
                        .DisplaySplash({ TitleText: 'Please wait', MessageText: 'Adding contact info...' })
                        .pipe(takeUntil(self._subrelease))
                        .subscribe(() => {
                            self.appService.AddCollegeContact(self.User!.AuthToken, collegeID, email).subscribe({
                                next: (contacts: CollegeContactInfo[]) => {
                                    // Toast the success
                                    self.splashService.CloseSplash();
                                    self.splashService.SnackbarSuccess('New college contact added successfully.');

                                    // Update the list of college contacts
                                    self.UpdateCollegeContactsDisplay(contacts);

                                    // Reset the course form
                                    self.CollegeContactForm.reset();

                                    // Hide the panel
                                    self.ShowingContactEntryPanel = false;
                                },
                                error: (error: Error) => self.errorService.HandleServiceError(error)
                            });
                        });
                }
            });
    }

    // #endregion

    // #region Remove College Contact Method

    RemoveCollegeContact(cc: CollegeContactInfo) {
        const self = this;

        if (!cc.CollegeContactID) {
            return;
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You have chosen to remove the following college contact from the system:<br /><br />
                        <strong>College: </strong>${cc.CollegeName}<br />
                        <strong>Email: </strong>${cc.Email}<br /><br />
                    Once removed, this contact info will be removed from the system and the email associated with it will no 
                    longer receive college advisor email notifications.
                    <br /><br />
                    Are you sure you wish to continue?`,
                OKText: 'Yes - Remove Contact',
                CancelText: 'No - CANCEL',
            })
            .subscribe((result: boolean) => {
                if (result) {
                // User has opted to proceed. Signal the system to remove this term window from the college.
                    self.appService.RemoveCollegeContact(this.User!.AuthToken, cc.CollegeContactID).subscribe({
                        next: (contacts: CollegeContactInfo[]) => {
                            // Toast the success
                            self.splashService.CloseSplash();
                            self.splashService.SnackbarSuccess('College contact was removed successfully.');

                            // Update the list of eligible courses
                            self.UpdateCollegeContactsDisplay(contacts);
                        },
                        error: (error: Error) => self.errorService.HandleServiceError(error)
                    });
                }
            });
    }

    // #endregion

    // #region Reset New User Form Method

    ResetCollegeContact() {
        this.CollegeContactForm.reset();
        this.ShowingContactEntryPanel = false;
    }

    // #endregion

    // #region Show Contact Info Editor Panel

    ShowContactInfoEditorPanel(contact?: CollegeContactInfo) {
        const self = this;

        // Set up the formgroup to prepare for editing. If we were provided a college term, use those values to prefill the formgroup.
        if (contact) {
            const newform = {
                CollegeContactID: contact.CollegeContactID,
                CollegeID: contact.CollegeID,
                Email: contact.Email,
            };

            // Patch the values
            self.CollegeContactForm.patchValue(newform);
            self.IsEditContact = true;
        } else {
            // Clear the form
            const newform = {
                CollegeContactID: null,
                CollegeID: null,
                Email: "",
            };

            self.CollegeContactForm.patchValue(newform);
            self.IsEditContact = false;
        }

        self.CollegeContactForm.updateValueAndValidity({ emitEvent: false });

        self.ShowingContactEntryPanel = true;
    }

    // #endregion

    // #region Update College Contact Handler Method

    UpdateCollegeContactHandler() {
        const self = this;

        const form = <CollegeContactForm>self.CollegeContactForm.getRawValue();
        if (!form) {
            return;
        }

        const collegeID = form.CollegeID;
        const email = form.Email;

        if (!form.CollegeID) {
            self.splashService.SnackbarError('Selected college is either missing or invalid.');
            return;
        }

        if (!form.Email) {
            self.splashService.SnackbarError('Email provided is missing.');
            return;
        }

        const college = self.Colleges.find(x => x.CollegeID == collegeID)!.CollegeName;

        if (!college) {
            return self.splashService.SnackbarError(`Could not find college with id ${collegeID}`);
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: `You are about to update the following college contact info: <br /><br />
                                <strong>College: </strong>${college}<br />
                                <strong>Email: </strong>${email}<br />
                            <br /><br />Are you sure you wish to continue?`,
                OKText: 'Yes - Update Contact',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the college contact.
                    self.appService.UpdateCollegeContact(
                        self.User!.AuthToken,
                        form.CollegeContactID,
                        form.CollegeID,
                        form.Email
                    )
                    .subscribe({
                        next: contacts => {
                            // Toast the success
                            self.splashService.SnackbarSuccess('College contact info updated successfully.');

                            // Update the list of known college contacts
                            self.CollegeContacts = contacts;
                            self.CollegeContactData.Update(contacts);

                            // Reset the term form
                            self.CollegeContactForm.reset();

                            // Hide the panel
                            self.ShowingContactEntryPanel = false;
                        },
                        error: (error: Error) => self.errorService.HandleServiceError(error)
                    });
                }
            });
    }

    // #endregion

    // #region Update College Contacts Display Method
    
    UpdateCollegeContactsDisplay(contacts: CollegeContactInfo[]) {
        const self = this;

        self.CollegeContacts = contacts;

        if (!self.CollegeContacts || !self.CollegeContacts.length) {
            // self.NoData = true;

            // Put in a fake record to trip the display message
            self.CollegeContactData.Update([]);
        } else {
            // self.NoData = false;
            self.CollegeContactData.Update(self.CollegeContacts);
        }
    }

    // #endregion
}

export interface CollegeContactForm {
    CollegeContactID?: number,
    CollegeID: number,
    Email: string
}


// WEBPACK FOOTER //
// ./src/app/admin-settings/components/college-contact/college-contact.component.ts